import { useMemo } from 'react';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PurchaseOrderMetadata } from '@/components/accounts-payable/purchase-order-detail/purchase-order-metadata';
import { PurchaseOrderLineItems } from '@/components/accounts-payable/purchase-order-detail/purchase-order-line-items';
import { ChargesTable } from '@/components/common/charges-table';
import { useChargesForEntity } from '@/api/rpc/hooks/finance/useCharges';
import { SystemEntity } from '@erp/shared-types';
import type { PurchaseOrder, PurchaseOrderLineItem } from '@erp/rpc';

export interface PurchaseOrderDetailsProps {
  purchaseOrder: PurchaseOrder;
  lineItems?: PurchaseOrderLineItem[];
}

export function PurchaseOrderDetails({
  purchaseOrder,
  lineItems,
}: PurchaseOrderDetailsProps) {
  // Fetch charges for the purchase order
  const { charges: allCharges } = useChargesForEntity({
    entityType: SystemEntity.INTERNAL_PURCHASE_ORDER,
    entityId: purchaseOrder.id,
    includeLineCharges: false, // Only header charges
  });

  // Filter to only header charges (no docLineId)
  const headerCharges = useMemo(
    () => allCharges.filter((charge) => !charge.docLineId),
    [allCharges],
  );

  const hasPaymentHoldLines = useMemo(
    () => lineItems?.some((li) => li.paymentHold) ?? false,
    [lineItems],
  );

  return (
    <div className="h-full flex flex-col space-y-4 overflow-y-auto px-3">
      <PurchaseOrderMetadata
        purchaseOrder={purchaseOrder}
        hasPaymentHoldLines={hasPaymentHoldLines}
      />

      {/* Tabs for Line Items and Charges */}
      {(lineItems && lineItems.length > 0) || headerCharges.length > 0 ? (
        <>
          <Separator />
          <Tabs
            defaultValue={
              lineItems && lineItems.length > 0 ? 'line-items' : 'charges'
            }
            className="w-full"
          >
            <TabsList className="w-full">
              {lineItems && lineItems.length > 0 && (
                <TabsTrigger value="line-items">
                  Line Items ({lineItems.length})
                </TabsTrigger>
              )}
              {headerCharges.length > 0 && (
                <TabsTrigger value="charges">
                  Charges ({headerCharges.length})
                </TabsTrigger>
              )}
            </TabsList>
            {lineItems && lineItems.length > 0 && (
              <TabsContent value="line-items" className="mt-0">
                <PurchaseOrderLineItems
                  lineItems={lineItems}
                  currency={purchaseOrder.currency}
                />
              </TabsContent>
            )}
            {headerCharges.length > 0 && (
              <TabsContent value="charges" className="mt-0">
                <div className="px-3">
                  <ChargesTable
                    charges={headerCharges}
                    currency={purchaseOrder.currency ?? 'USD'}
                  />
                </div>
              </TabsContent>
            )}
          </Tabs>
        </>
      ) : null}
    </div>
  );
}
